import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';

import { type TStore } from '#core/store';
import { rtkApi } from '#adapter/api/campaigns/v5/configvalues';

import { IItem } from '#route/campaigns/create/types';

export type TItemOrUndefined = IItem | undefined;

const listItemsAdapter = createEntityAdapter<TItemOrUndefined>({
    selectId: (item) => item?.nm || 0
});

interface IState {
    itemsList: EntityState<TItemOrUndefined>,
    removedItems: EntityState<TItemOrUndefined>,
    cpmMinBooster?: number
}

const initialState: IState = {
    itemsList    : listItemsAdapter.getInitialState(),
    removedItems : listItemsAdapter.getInitialState(),
    cpmMinBooster: undefined
};

export const autoListItemsSlice = createSlice({
    name    : 'auto-list-items',
    initialState,
    reducers: {
        setAllItems: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.setAll(state.itemsList, action.payload);
        },
        itemsToRemoveReset: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.addMany(state.removedItems, action.payload);
            listItemsAdapter.removeMany(state.itemsList, action.payload.map((item) => item?.nm ?? 0));
        },
        addItems: (state, action: PayloadAction<Array<TItemOrUndefined>>) => {
            listItemsAdapter.addMany(state.itemsList, action.payload);
            listItemsAdapter.removeMany(state.removedItems, action.payload.map((item) => item?.nm ?? 0));
        },
        clearRemovesItems: (state) => {
            listItemsAdapter.removeAll(state.removedItems);
        },
        updateItem: (state, action: PayloadAction<Pick<IItem, 'bet' | 'nm' | 'hasBiggestBet'>>) => {
            listItemsAdapter.updateOne(state.itemsList, { id: action.payload.nm, changes: action.payload });
        },
        updateItems: (state, action: PayloadAction<Array<Pick<IItem, 'bet' | 'nm'>>>) => {
            listItemsAdapter.updateMany(state.itemsList, action.payload.map((item) => ({ id: item.nm, changes: item })));
        }
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(rtkApi.endpoints.getApiV5Configvalues.matchFulfilled, (state, { payload }) => ({
                ...state,
                cpmMinBooster: payload.cpmMinBooster
            }));
    }
});

export const itemsListSelector = listItemsAdapter.getSelectors<TStore>((store) => store[autoListItemsSlice.name].itemsList);
export const removedItemsSelector = listItemsAdapter.getSelectors<TStore>((store) => store[autoListItemsSlice.name].removedItems);

export const { actions, reducer } = autoListItemsSlice;
